import { isAccountAbortError } from '../boot/accountCatalogCache.js';
import {
  DEFAULT_LIMIT,
  categoryIncludesSportsProvider,
  combineSourcePages,
  isOpenSportsRow,
  isSettledGameRow,
  isSettledSportsRow,
  isUnsettledGameRow,
  matchesHistoryCategory,
  presentGameRow,
  presentSportsRow,
  shouldFetchGames,
  shouldFetchSports,
  sortBettingRows,
} from './helpers.js';

export function bettingHistoryQueryKey(query) {
  return [
    query.tab,
    query.page,
    query.from,
    query.to,
    query.gameType,
    query.provider,
    query.reload,
  ].join('|');
}

export function sportsHistoryIncluded(query, providersByGameType) {
  return shouldFetchSports(query.provider)
    && categoryIncludesSportsProvider(query.gameType, providersByGameType);
}

export function bettingHistoryRequestParams(query) {
  const settled = query.tab === 'settled';
  return {
    sportsParams: {
      page: query.page,
      limit: DEFAULT_LIMIT,
      from: query.from,
      to: query.to,
      status: settled ? 'settled' : 'open',
    },
    gameParams: {
      page: query.page,
      limit: DEFAULT_LIMIT,
      from: query.from,
      to: query.to,
      type: 'Game',
      category: query.gameType,
      provider: query.provider,
      ...(settled ? {} : { result: 'BET' }),
    },
  };
}

function emptyPage() {
  return { records: [], totalPages: 1, total: 0 };
}

export function presentBettingHistory(sportsPage, gamePage, query) {
  const settled = query.tab === 'settled';
  const sportsRows = (sportsPage?.records || [])
    .filter((row) => (settled ? isSettledSportsRow(row) : isOpenSportsRow(row)))
    .map(presentSportsRow)
    .filter((row) => matchesHistoryCategory(row, query.gameType));
  const gameRows = (gamePage?.records || [])
    .filter((row) => (settled ? isSettledGameRow(row) : isUnsettledGameRow(row)))
    .map(presentGameRow)
    .filter((row) => matchesHistoryCategory(row, query.gameType));
  return {
    rows: sortBettingRows(sportsRows.concat(gameRows)),
    totalPages: combineSourcePages(sportsPage, gamePage).totalPages,
  };
}

export function createBettingHistorySession({ fetchGames, fetchSports, onChange }) {
  let generation = 0;
  let query = null;
  let providers = {};
  let sportsStarted = false;
  let gamesIncluded = false;
  let gamesFailed = false;
  let gamePage = emptyPage();
  let sportsPage = emptyPage();
  let gamesCalls = 0;
  let sportsCalls = 0;
  const listeners = new Set();
  const state = {
    rows: [],
    totalPages: 1,
    loading: true,
    error: '',
    sportsError: '',
    sportsPending: false,
  };

  function emit() {
    const next = snapshot();
    listeners.forEach((fn) => fn(next));
    if (typeof onChange === 'function') onChange(next);
  }

  function snapshot() {
    return {
      rows: state.rows.slice(),
      totalPages: state.totalPages,
      loading: state.loading,
      error: state.error,
      sportsError: state.sportsError,
      sportsPending: state.sportsPending,
      gamesCalls,
      sportsCalls,
    };
  }

  function publish() {
    if (!query || gamesFailed) return;
    const view = presentBettingHistory(sportsPage, gamePage, query);
    state.rows = view.rows;
    state.totalPages = view.totalPages;
  }

  function startSports(gen) {
    if (!query || sportsStarted || !sportsHistoryIncluded(query, providers)) return;
    sportsStarted = true;
    state.sportsPending = true;
    sportsCalls += 1;
    const params = bettingHistoryRequestParams(query).sportsParams;
    Promise.resolve()
      .then(() => fetchSports(params))
      .then((page) => {
        if (gen !== generation || gamesFailed) return;
        sportsPage = page || emptyPage();
        state.sportsPending = false;
        state.sportsError = '';
        if (!gamesIncluded) state.loading = false;
        publish();
        emit();
      })
      .catch((err) => {
        if (gen !== generation || isAccountAbortError(err)) return;
        state.sportsPending = false;
        if (gamesFailed) {
          emit();
          return;
        }
        const message = err?.message || 'Failed to load';
        if (gamesIncluded && !gamesFailed) {
          state.sportsError = message;
          if (!state.loading) publish();
        } else {
          state.loading = false;
          state.error = message;
          state.rows = [];
        }
        emit();
      });
  }

  return {
    snapshot,
    subscribe(fn) {
      listeners.add(fn);
      fn(snapshot());
      return () => listeners.delete(fn);
    },
    setProviders(next) {
      providers = next && typeof next === 'object' ? next : {};
    },
    applyUserQuery(nextQuery) {
      generation += 1;
      const gen = generation;
      query = nextQuery;
      sportsStarted = false;
      gamesFailed = false;
      gamesIncluded = shouldFetchGames(nextQuery.provider);
      gamePage = emptyPage();
      sportsPage = emptyPage();
      state.rows = [];
      state.totalPages = 1;
      state.error = '';
      state.sportsError = '';
      state.sportsPending = false;
      state.loading = true;
      emit();
      if (gamesIncluded) {
        gamesCalls += 1;
        const params = bettingHistoryRequestParams(nextQuery).gameParams;
        Promise.resolve()
          .then(() => fetchGames(params))
          .then((page) => {
            if (gen !== generation) return;
            gamePage = page || emptyPage();
            state.loading = false;
            state.error = '';
            publish();
            emit();
          })
          .catch((err) => {
            if (gen !== generation || isAccountAbortError(err)) return;
            gamesFailed = true;
            state.loading = false;
            state.error = err?.message || 'Failed to load';
            state.rows = [];
            state.sportsPending = false;
            emit();
          });
      }
      if (sportsHistoryIncluded(nextQuery, providers)) startSports(gen);
      else if (!gamesIncluded) {
        state.loading = false;
        emit();
      }
    },
    applyMetadata(nextProviders) {
      this.setProviders(nextProviders);
      if (!query || sportsStarted || gamesFailed) return snapshot();
      if (!sportsHistoryIncluded(query, providers)) return snapshot();
      startSports(generation);
      emit();
      return snapshot();
    },
    releaseGameLoading() {
      if (!state.loading) return snapshot();
      state.loading = false;
      emit();
      return snapshot();
    },
    cancel() {
      generation += 1;
    },
  };
}
